import { Directive, OnDestroy } from '@angular/core';

import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';

import { PreloaderClassNames } from '@hdms/shared/common-types';
import { PlatformService, WindowRefService } from '@hdms/shared/util-core';

export enum PreloaderSchematicType {
  PARAGRAPH = 'paragraph',
  FRAME = 'frame',
  NONE = 'none'
}

export enum PreloaderSize {
  XS = 'x-small',
  S = 'small',
  M = 'medium'
}

@Directive()
export abstract class PreloaderComponentAbstract implements OnDestroy {
  static transitionEnd = 'transitionend';
  static debounceTime = 200;
  static hideTimeout = 600;

  protected loadingStatus: Subscription;
  protected preloaderElement: HTMLElement;
  protected transitionEndHandler: EventListener;
  protected readonly win: any;
  protected hideTimer: number;
  protected debounceTime = PreloaderComponentAbstract.debounceTime;

  isActive = false;

  protected constructor(winRef: WindowRefService, platformService: PlatformService) {
    this.win = winRef.nativeWindow;

    if (platformService.isServer) {
      this.debounceTime = 0;
      PreloaderComponentAbstract.hideTimeout = 0;
    }

    this.transitionEndHandler = this.resetClassNames.bind(this);
  }

  protected createStatusSubscription(subject: Subject<boolean>) {
    this.loadingStatus = subject.pipe(debounceTime(this.debounceTime), distinctUntilChanged()).subscribe(status => {
      status ? this.show() : this.hide();
    });
  }

  protected show() {
    this.resetClassNames(null);

    this.isActive = true;

    this.win.requestAnimationFrame(() => {
      this.preloaderElement.classList.add(PreloaderClassNames.ACTIVE);

      // we need some delay for animation change to get recognized
      this.win.requestAnimationFrame(() => {
        this.preloaderElement.classList.add(PreloaderClassNames.SHOW);
      });
    });
  }

  protected hide() {
    this.preloaderElement.addEventListener(PreloaderComponentAbstract.transitionEnd, this.transitionEndHandler);

    this.win.requestAnimationFrame(() => {
      this.preloaderElement.classList.remove(PreloaderClassNames.INITIAL, PreloaderClassNames.SHOW);
      this.preloaderElement.classList.add(PreloaderClassNames.HIDE);

      this.hideTimer = this.win.setTimeout(() => {
        this.resetClassNames(new CustomEvent('transitiontimeout'));
      }, PreloaderComponentAbstract.hideTimeout);
    });
  }

  protected resetClassNames(event: Event) {
    this.preloaderElement.classList.remove(PreloaderClassNames.HIDE);
    this.preloaderElement.removeEventListener(PreloaderComponentAbstract.transitionEnd, this.transitionEndHandler);

    if (event) {
      clearTimeout(this.hideTimer);
      this.preloaderElement.classList.remove(PreloaderClassNames.ACTIVE);
      this.isActive = false;
    }
  }

  ngOnDestroy() {
    this.loadingStatus && this.loadingStatus.unsubscribe();
    clearTimeout(this.hideTimer);
  }
}
